/**
 * Мультиплеерный бой Glitchlings через WebSocket
 * Canvas-based UI с джойстиками для управления
 */

// Эмодзи героев
const EMOJIS = {
  bitcor: '🦉',
  noizling: '🔊',
  forkbomb: '💥',
  zalgoth: '🔮',
  errorborn: '☠️'
};

// Цвета команд
const TEAM_COLORS = {
  blue: '#4dfffd',
  red: '#ff6666'
};

/**
 * Создаёт менеджер мультиплеерного боя
 */
export function createMultiplayerBattleManager({
  containerId = 'multiplayer-battle-container',
  onBattleEnd,
  getTelegramInitData
}) {
  const container = document.getElementById(containerId);
  if (!container) {
    console.error(`[MultiplayerBattle] Container ${containerId} not found`);
    return null;
  }

  let socket = null;
  let playerId = null;
  let localPlayer = null;
  let remotePlayers = {};
  let canvas = null;
  let ctx = null;
  let moveVector = { x: 0, y: 0 };
  let aimVector = { x: 1, y: 0 };
  let roomId = null;
  let team = null; // Сохраняем команду игрока для определения победителя
  let battleStarted = false;
  let currentHero = null; // Сохраняем выбранного героя
  
  // Хранилище для очистки ресурсов (предотвращение утечек памяти)
  const cleanup = {
    resizeHandler: null,
    joystickHandlers: [],
    buttonHandlers: [],
    intervals: [],
    timeouts: [],
    socketHandlers: {}
  };

  // Джойстики
  let moveJoystick = null;
  let aimJoystick = null;
  let moveStick = null;
  let aimStick = null;
  let ultButton = null;
  let gadgetButton = null;
  let gadgetCooldown = 0;
  
  // Визуальные эффекты
  let visualEffects = []; // { type, x, y, angle, lifetime, maxLifetime, damage?, color? }
  let damageNumbers = []; // { x, y, damage, lifetime, color }
  let projectiles = []; // { id, x, y, vx, vy, team, damage }
  let obstacles = []; // { x, y, width, height, type }
  let storm = null; // { centerX, centerY, currentRadius, active }
  
  // Интерполяция
  let playerHistory = {}; // playerId → [{ x, y, timestamp }, ...]
  const INTERPOLATION_DELAY = 100; // мс задержки для интерполяции
  
  // Загрузочный экран
  let loadingScreen = null;
  let loadingProgress = 0;
  let readyScreen = null; // Экран подтверждения готовности
  let gameModeScreen = null; // Экран выбора режима игры (PvP/PvE)
  let searchCancelled = false; // Флаг отмены поиска
  let selectedGameMode = null; // Выбранный режим игры

  const MAP_WIDTH = 1280;
  const MAP_HEIGHT = 960;
  let VIEW_WIDTH = 0;
  let VIEW_HEIGHT = 0;

  /**
   * Инициализирует canvas
   */
  function initCanvas() {
    canvas = document.createElement('canvas');
    canvas.id = 'multiplayer-canvas';
    canvas.style.display = 'block';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.background = '#0a0a1a'; // Тёмный фон (основной фон рисуется на canvas)
    canvas.style.position = 'absolute';
    canvas.style.top = '0';
    canvas.style.left = '0';
    canvas.style.touchAction = 'none';
    canvas.style.pointerEvents = 'none'; // Canvas не должен перехватывать клики

    container.appendChild(canvas);

    // Устанавливаем размеры canvas
    function resizeCanvas() {
      VIEW_WIDTH = window.innerWidth;
      VIEW_HEIGHT = window.innerHeight;
      canvas.width = VIEW_WIDTH;
      canvas.height = VIEW_HEIGHT;
    }

    resizeCanvas();
    cleanup.resizeHandler = resizeCanvas;
    window.addEventListener('resize', cleanup.resizeHandler);

    ctx = canvas.getContext('2d');
  }

  /**
   * Создаёт UI элементы (джойстики, кнопки)
   */
  function createUI() {
    const uiContainer = document.createElement('div');
    uiContainer.id = 'multiplayer-ui';
    uiContainer.style.cssText = `
      position: absolute;
      bottom: 20px;
      width: 100%;
      display: flex;
      justify-content: space-between;
      padding: 0 20px;
      pointer-events: none;
      z-index: 100;
    `;

    // Джойстик движения
    moveJoystick = document.createElement('div');
    moveJoystick.className = 'multiplayer-joystick';
    moveJoystick.id = 'move-joystick';
    moveJoystick.style.cssText = `
      width: 120px;
      height: 120px;
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-radius: 50%;
      position: relative;
      pointer-events: auto;
      background: rgba(0, 0, 0, 0.3);
    `;

    moveStick = document.createElement('div');
    moveStick.className = 'multiplayer-stick';
    moveStick.id = 'move-stick';
    moveStick.style.cssText = `
      width: 50px;
      height: 50px;
      background: rgba(255, 255, 255, 0.7);
      border-radius: 50%;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      transition: transform 0.1s ease;
    `;

    moveJoystick.appendChild(moveStick);

    // Джойстик прицеливания (скрыт - используется автоматическое наведение)
    // Оставляем для совместимости, но скрываем визуально
    aimJoystick = document.createElement('div');
    aimJoystick.className = 'multiplayer-joystick';
    aimJoystick.id = 'aim-joystick';
    aimJoystick.style.cssText = `
      width: 120px;
      height: 120px;
      border: 2px solid rgba(255, 46, 0, 0.3);
      border-radius: 50%;
      position: relative;
      pointer-events: none; /* Отключаем взаимодействие - используем автонаведение */
      background: rgba(0, 0, 0, 0);
      opacity: 0; /* Скрываем визуально */
      display: none; /* Полностью скрываем */
    `;

    aimStick = document.createElement('div');
    aimStick.className = 'multiplayer-stick';
    aimStick.id = 'aim-stick';
    aimStick.style.cssText = `
      width: 50px;
      height: 50px;
      background: rgba(255, 46, 0, 0.7);
      border-radius: 50%;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      transition: transform 0.1s ease;
      display: none; /* Скрываем */
    `;

    aimJoystick.appendChild(aimStick);

    // Кнопка ульты
    ultButton = document.createElement('button');
    ultButton.id = 'ult-btn';
    ultButton.style.cssText = `
      position: absolute;
      bottom: 150px;
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
      height: 80px;
      background: linear-gradient(135deg, #ff4d4d, #FF2E00);
      border-radius: 50%;
      color: white;
      font-weight: bold;
      pointer-events: auto;
      border: none;
      font-size: 12px;
      cursor: pointer;
      box-shadow: 0 4px 12px rgba(255, 46, 0, 0.4);
      z-index: 100;
      touch-action: manipulation;
      user-select: none;
    `;
    ultButton.innerHTML = 'ULT<br>0%';
    ultButton.disabled = true;

    // Кнопка гаджета (слева от ульта)
    gadgetButton = document.createElement('button');
    gadgetButton.id = 'gadget-btn';
    gadgetButton.style.cssText = `
      position: absolute;
      bottom: 150px;
      left: 20px;
      width: 80px;
      height: 80px;
      background: linear-gradient(135deg, #00C8FF, #0088cc);
      border-radius: 50%;
      color: white;
      font-weight: bold;
      pointer-events: auto;
      border: none;
      font-size: 12px;
      cursor: pointer;
      box-shadow: 0 4px 12px rgba(0, 200, 255, 0.4);
      z-index: 100;
      touch-action: manipulation;
      user-select: none;
    `;
    gadgetButton.innerHTML = '⚡<br>GADGET';
    gadgetButton.disabled = false;

    // Кнопка атаки (красная справа)
    const attackButton = document.createElement('button');
    attackButton.id = 'attack-btn';
    attackButton.style.cssText = `
      position: absolute;
      bottom: 20px;
      right: 20px;
      width: 120px;
      height: 120px;
      background: radial-gradient(circle, #ff4d4d, #cc0000);
      border-radius: 50%;
      color: white;
      font-weight: bold;
      pointer-events: auto;
      border: 4px solid rgba(255, 255, 255, 0.3);
      font-size: 16px;
      cursor: pointer;
      box-shadow: 0 4px 12px rgba(255, 0, 0, 0.5);
      z-index: 100;
      touch-action: manipulation;
      user-select: none;
      display: flex;
      align-items: center;
      justify-content: center;
    `;
    attackButton.innerHTML = '⚔️<br>АТАКА';
    attackButton.disabled = false;

    uiContainer.appendChild(moveJoystick);
    uiContainer.appendChild(aimJoystick);
    container.appendChild(uiContainer);
    container.appendChild(ultButton);
    container.appendChild(gadgetButton);
    container.appendChild(attackButton);

    // Настройка джойстиков
    setupJoystick(moveJoystick, moveStick, (x, y) => {
      moveVector.x = x;
      moveVector.y = y;
    });

    // Джойстик прицеливания отключен - используется автоматическое наведение
    // aimVector устанавливается автоматически на основе движения
    setupJoystick(aimJoystick, aimStick, (x, y) => {
      // Не используется - автонаведение работает через движение
      aimVector.x = moveVector.x || 1; // Используем движение или направление по умолчанию
      aimVector.y = moveVector.y || 0;
    });

    // Обработчик ульты
    // Обработчик ульта (предотвращаем двойную отправку)
    let lastUltTime = 0;
    const ULT_COOLDOWN = 200;
    
    const ultHandler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      const now = Date.now();
      if (now - lastUltTime < ULT_COOLDOWN) {
        return;
      }
      
      if (socket && socket.readyState === WebSocket.OPEN && localPlayer && localPlayer.energy >= 100) {
        socket.send(JSON.stringify({ type: 'ultimate' }));
        lastUltTime = now;
        console.log('[MultiplayerBattle] Ульт использован');
      }
    };
    
    // Используем только touchstart на мобильных, click на десктопе
    if ('ontouchstart' in window) {
      ultButton.addEventListener('touchstart', ultHandler, { passive: false });
      cleanup.buttonHandlers.push({ element: ultButton, type: 'touchstart', handler: ultHandler });
    } else {
      ultButton.addEventListener('click', ultHandler);
      cleanup.buttonHandlers.push({ element: ultButton, type: 'click', handler: ultHandler });
    }

    // Обработчик атаки (предотвращаем двойную отправку на мобильных)
    let lastAttackTime = 0;
    const ATTACK_COOLDOWN = 100; // Минимальный интервал между атаками (мс)
    
    const attackHandler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      const now = Date.now();
      if (now - lastAttackTime < ATTACK_COOLDOWN) {
        // Предотвращаем двойную отправку
        return;
      }
      
      if (socket && socket.readyState === WebSocket.OPEN && localPlayer && battleStarted) {
        socket.send(JSON.stringify({ type: 'attack' }));
        lastAttackTime = now;
        console.log('[MultiplayerBattle] Атака');
      }
    };
    
    // Используем только touchstart на мобильных, click на десктопе
    if ('ontouchstart' in window) {
      attackButton.addEventListener('touchstart', attackHandler, { passive: false });
      cleanup.buttonHandlers.push({ element: attackButton, type: 'touchstart', handler: attackHandler });
    } else {
      attackButton.addEventListener('click', attackHandler);
      cleanup.buttonHandlers.push({ element: attackButton, type: 'click', handler: attackHandler });
    }

    // Обработчик гаджета (предотвращаем двойную отправку)
    let lastGadgetTime = 0;
    const GADGET_COOLDOWN = 200;
    
    const gadgetHandler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      const now = Date.now();
      if (now - lastGadgetTime < GADGET_COOLDOWN) {
        return;
      }
      
      console.log('[MultiplayerBattle] Клик по гаджету:', {
        socketReady: socket?.readyState === WebSocket.OPEN,
        localPlayer: !!localPlayer,
        battleStarted,
        gadgetCooldown,
        hero: localPlayer?.hero
      });
      
      if (socket && socket.readyState === WebSocket.OPEN && localPlayer && battleStarted && gadgetCooldown <= 0) {
        const message = { type: 'gadget' };
        socket.send(JSON.stringify(message));
        console.log('[MultiplayerBattle] ✅ Гаджет отправлен на сервер:', message);
        // Временно блокируем кнопку для визуальной обратной связи
        gadgetButton.disabled = true;
        const timeoutId = setTimeout(() => {
          if (gadgetButton && gadgetCooldown <= 0) {
            gadgetButton.disabled = false;
          }
        }, 100);
        cleanup.timeouts.push(timeoutId);
      } else {
        console.error('[MultiplayerBattle] ❌ Гаджет не может быть использован:', {
          socket: socket?.readyState,
          socketOpen: socket?.readyState === WebSocket.OPEN,
          localPlayer: !!localPlayer,
          battleStarted,
          gadgetCooldown,
          hero: localPlayer?.hero
        });
      }
      
      lastGadgetTime = now;
    };
    
    // Используем только touchstart на мобильных, click на десктопе
    if ('ontouchstart' in window) {
      gadgetButton.addEventListener('touchstart', gadgetHandler, { passive: false });
      cleanup.buttonHandlers.push({ element: gadgetButton, type: 'touchstart', handler: gadgetHandler });
    } else {
      gadgetButton.addEventListener('click', gadgetHandler);
      cleanup.buttonHandlers.push({ element: gadgetButton, type: 'click', handler: gadgetHandler });
    }
    
    // Сохраняем ссылку на кнопку атаки
    window.__attackButton = attackButton;
  }

  /**
   * Настраивает джойстик
   */
  function setupJoystick(joystick, stick, onMove) {
    let isActive = false;
    const maxDistance = 35; // Максимальное расстояние от центра

    function updateStick(x, y) {
      const rect = joystick.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      const dx = x - centerX;
      const dy = y - centerY;
      const distance = Math.min(Math.sqrt(dx * dx + dy * dy), maxDistance);
      const angle = Math.atan2(dy, dx);

      const stickX = Math.cos(angle) * distance;
      const stickY = Math.sin(angle) * distance;

      stick.style.transform = `translate(calc(-50% + ${stickX}px), calc(-50% + ${stickY}px))`;

      // Нормализуем вектор
      const normalizedX = distance > 0 ? Math.cos(angle) : 0;
      const normalizedY = distance > 0 ? Math.sin(angle) : 0;

      onMove(normalizedX, normalizedY);
    }

    function handleStart(e) {
      isActive = true;
      const touch = e.touches ? e.touches[0] : e;
      updateStick(touch.clientX, touch.clientY);
    }

    function handleMove(e) {
      if (!isActive) return;
      e.preventDefault();
      const touch = e.touches ? e.touches[0] : e;
      updateStick(touch.clientX, touch.clientY);
    }

    function handleEnd() {
      isActive = false;
      stick.style.transform = 'translate(-50%, -50%)';
      onMove(0, 0);
    }

    // Обработчики для джойстика
    joystick.addEventListener('mousedown', handleStart);
    joystick.addEventListener('touchstart', handleStart, { passive: false });
    
    // Глобальные обработчики для движения
    const moveHandler = (e) => {
      if (isActive) {
        handleMove(e);
      }
    };
    
    const endHandler = (e) => {
      if (!isActive) return;
      
      // Проверяем, что событие не пришло от кнопки атаки или других UI элементов
      const target = e.target;
      if (target && (
        target.id === 'attack-btn' ||
        target.closest('#attack-btn') ||
        target.id === 'ult-btn' ||
        target.closest('#ult-btn') ||
        target.id === 'gadget-btn' ||
        target.closest('#gadget-btn')
      )) {
        // Игнорируем события от кнопок - не сбрасываем джойстик
        return;
      }
      
      handleEnd(e);
    };
    
    document.addEventListener('mousemove', moveHandler, { passive: false });
    document.addEventListener('touchmove', moveHandler, { passive: false });
    document.addEventListener('mouseup', endHandler);
    document.addEventListener('touchend', endHandler, { passive: false });
    document.addEventListener('touchcancel', endHandler, { passive: false });
    
    // Сохраняем обработчики для последующего удаления
    const handlers = {
      joystick: joystick,
      handleStart: handleStart,
      moveHandler: moveHandler,
      endHandler: endHandler
    };
    cleanup.joystickHandlers.push(handlers);
  }

  /**
   * Подключается к WebSocket серверу
   */
  function connectToServer(hero) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws/battle`;
    
    // Получаем initData для аутентификации
    const initData = getTelegramInitData ? getTelegramInitData() : null;
    let url = `${wsUrl}?hero=${hero}`;
    
    if (initData) {
      // Добавляем initData как query параметр
      url += `&initData=${encodeURIComponent(initData)}`;
    }

    console.log('[MultiplayerBattle] Подключение к WebSocket:', {
      url: url.substring(0, 100) + '...', // Логируем только начало URL для безопасности
      hasInitData: !!initData,
      protocol: protocol
    });

    try {
      socket = new WebSocket(url);

      let errorShown = false;
      
      cleanup.socketHandlers.onopen = () => {
        console.log('[MultiplayerBattle] ✅ WebSocket подключение установлено, ждём сообщение "connected" от сервера');
        // Не обновляем прогресс - ждём сообщения 'connected' для показа экрана выбора режима
      };

      cleanup.socketHandlers.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          console.log('[MultiplayerBattle] 📨 Получено сообщение от сервера:', msg.type, msg);
          handleServerMessage(msg);
        } catch (error) {
          console.error('[MultiplayerBattle] Ошибка парсинга сообщения:', error);
        }
      };
      
      cleanup.socketHandlers.onerror = (error) => {
        console.error('[MultiplayerBattle] ❌ WebSocket ошибка:', {
          type: error.type,
          target: error.target?.readyState,
          url: error.target?.url?.substring(0, 100) + '...'
        });
        
        // Показываем пользователю понятное сообщение только один раз
        if (!errorShown) {
          errorShown = true;
          const telegramWebApp = window.Telegram?.WebApp;
          if (telegramWebApp?.showAlert) {
            try {
              telegramWebApp.showAlert('Не удалось подключиться к серверу. Проверьте интернет-соединение.');
            } catch (popupError) {
              // Игнорируем ошибку, если popup уже открыт
              console.warn('[MultiplayerBattle] Не удалось показать alert:', popupError);
            }
          }
        }
      };

      cleanup.socketHandlers.onclose = (event) => {
        console.log('[MultiplayerBattle] Соединение закрыто:', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean
        });
        
        if (onBattleEnd && event.code !== 1000) { // 1000 = нормальное закрытие
          onBattleEnd({ reason: 'disconnected', code: event.code });
        }
      };
      
      socket.onopen = cleanup.socketHandlers.onopen;
      socket.onmessage = cleanup.socketHandlers.onmessage;
      socket.onerror = cleanup.socketHandlers.onerror;
      socket.onclose = cleanup.socketHandlers.onclose;
    } catch (error) {
      console.error('[MultiplayerBattle] ❌ Ошибка создания WebSocket:', error);
      const telegramWebApp = window.Telegram?.WebApp;
      if (telegramWebApp?.showAlert) {
        try {
          telegramWebApp.showAlert('Ошибка подключения к серверу боя.');
        } catch (popupError) {
          // Игнорируем ошибку, если popup уже открыт
          console.warn('[MultiplayerBattle] Не удалось показать alert:', popupError);
        }
      }
    }
  }

  /**
   * Обрабатывает сообщения от сервера
   */
  function handleServerMessage(msg) {
    switch (msg.type) {
      case 'connected':
        // Подключено к серверу - показываем экран выбора режима
        console.log('[MultiplayerBattle] ✅ Подключено к серверу:', msg);
        playerId = msg.playerId;
        hideLoadingScreen();
        console.log('[MultiplayerBattle] Показываем экран выбора режима...');
        showGameModeScreen();
        console.log('[MultiplayerBattle] Экран выбора режима должен быть показан');
        break;
        
      case 'searching':
        // Поиск начат - показываем экран поиска
        console.log('[MultiplayerBattle] 🔍 Поиск матча:', msg.message);
        hideGameModeScreen();
        createLoadingScreen(currentHero || 'bitcor');
        updateLoadingText(msg.message || 'Поиск противников...');
        break;
        
      case 'init':
        playerId = msg.playerId;
        roomId = msg.roomId;
        team = msg.team;
        console.log('[MultiplayerBattle] Инициализация:', { playerId, roomId, team });
        
        // Обновляем прогресс загрузки
        if (updateLoadingProgress) {
          updateLoadingProgress(95);
        }
        
        updateGameState(msg.state);
        
        // Сохраняем препятствия, если они есть
        if (msg.obstacles) {
          obstacles = msg.obstacles;
        }
        
        // Сохраняем электрическое облако
        if (msg.storm) {
          storm = msg.storm;
        }
        break;

      case 'match_found':
        // Найден матч - показываем экран подтверждения готовности
        console.log('[MultiplayerBattle] Матч найден!', msg);
        hideLoadingScreen();
        showReadyScreen(msg);
        break;
        
      case 'ready_status':
        // Обновляем статус готовности других игроков
        console.log('[MultiplayerBattle] Статус готовности:', msg);
        updateReadyStatus(msg.readyCount, msg.totalCount);
        break;
        
      case 'match_cancelled':
        // Матч отменён - возвращаемся к поиску
        console.log('[MultiplayerBattle] Матч отменён:', msg.reason);
        hideReadyScreen();
        if (msg.reason === 'timeout' || msg.reason === 'player_cancelled') {
          // Показываем сообщение и продолжаем поиск
          createLoadingScreen(currentHero || 'bitcor');
          updateLoadingText('Поиск противников...');
        }
        break;
        
      case 'search_again':
        // Нужно искать заново
        console.log('[MultiplayerBattle] Поиск заново');
        hideReadyScreen();
        createLoadingScreen(currentHero || 'bitcor');
        updateLoadingText('Поиск противников...');
        break;
        
      case 'battle_start':
        battleStarted = true;
        console.log('[MultiplayerBattle] Бой начался!');
        
        // Обновляем состояние игры из сообщения
        if (msg.state) {
          updateGameState(msg.state);
        }
        
        // Скрываем экран готовности
        hideReadyScreen();
        
        // Скрываем загрузочный экран
        hideLoadingScreen();
        
        // Показываем canvas и UI и запускаем render loop
        setTimeout(() => {
          if (canvas) {
            canvas.style.display = 'block';
            // Убеждаемся, что canvas имеет правильные размеры
            const VIEW_WIDTH = window.innerWidth;
            const VIEW_HEIGHT = window.innerHeight;
            canvas.width = VIEW_WIDTH;
            canvas.height = VIEW_HEIGHT;
          }
          const uiContainer = document.getElementById('multiplayer-ui');
          if (uiContainer) {
            uiContainer.style.display = 'flex';
          }
          if (ultButton) {
            ultButton.style.display = 'block';
          }
          if (gadgetButton) {
            gadgetButton.style.display = 'block';
          }
          const attackBtn = document.getElementById('attack-btn');
          if (attackBtn) {
            attackBtn.style.display = 'block';
            attackBtn.disabled = false;
            attackBtn.style.opacity = '1';
          }
          
          // Запускаем render loop, если он ещё не запущен
          if (ctx && canvas && !window.__renderLoopActive) {
            window.__renderLoopActive = true;
            console.log('[MultiplayerBattle] Запуск render loop');
            render();
          }
        }, 300);
        break;

      case 'state':
        // Сохраняем старые позиции для визуальных эффектов
        const oldPlayers = { ...remotePlayers };
        const oldLocalHp = localPlayer?.hp;
        updateGameState(msg);
        
        // Обновляем снаряды
        if (msg.projectiles) {
          projectiles = msg.projectiles;
        }
        
        // Обновляем препятствия
        if (msg.obstacles) {
          obstacles = msg.obstacles;
        }
        
        // Обновляем электрическое облако
        if (msg.storm) {
          storm = msg.storm;
        }
        
        // Проверяем изменение HP локального игрока (для гаджета Bitcor)
        if (localPlayer && oldLocalHp && localPlayer.hp > oldLocalHp) {
          const healAmount = localPlayer.hp - oldLocalHp;
          console.log(`[MultiplayerBattle] 💚 Получено обновление HP: ${oldLocalHp} → ${localPlayer.hp} (+${healAmount})`);
        }
        
        // Создаём визуальные эффекты для атак (если есть изменения HP)
        if (msg.players) {
          for (const [id, newP] of Object.entries(msg.players)) {
            const oldP = oldPlayers[id];
            if (oldP && newP.hp < oldP.hp && newP.isActive) {
              // Игрок получил урон - создаём эффект
              const attacker = Object.values(msg.players).find(p => 
                p.id !== id && p.team !== newP.team && p.isActive && p.hp > 0
              );
              if (attacker) {
                const damage = oldP.hp - newP.hp;
                createAttackEffect(attacker, newP, damage);
              }
            }
          }
        }
        break;
      
      case 'projectile_hit':
        // Обрабатываем попадание снаряда
        if (msg.targetId === playerId) {
          // Локальный игрок получил урон
          console.log(`[MultiplayerBattle] 💥 Попадание снаряда! Урон: ${msg.damage}, HP: ${msg.newHp}`);
        }
        // Удаляем снаряд из массива
        projectiles = projectiles.filter(p => p.id !== msg.projectileId);
        break;
        
      case 'storm_damage':
        // Урон от электрического облака
        if (msg.newHp !== undefined && localPlayer) {
          const oldHp = localPlayer.hp;
          localPlayer.hp = msg.newHp;
          const damage = oldHp - msg.newHp;
          console.log(`[MultiplayerBattle] ⚡ Урон от электрического облака! Урон: ${damage}, HP: ${oldHp} → ${msg.newHp}`);
          
          // Создаём визуальный эффект урона от облака
          if (damage > 0) {
            createStormDamageEffect(localPlayer.x, localPlayer.y, damage);
          }
        }
        break;
        
      case 'storm_damage_visual':
        // Визуальный эффект урона от облака для других игроков
        const targetPlayer = remotePlayers[msg.playerId];
        if (targetPlayer && msg.damage > 0) {
          createStormDamageEffect(msg.x, msg.y, msg.damage);
        }
        break;

      case 'battle_end':
        battleStarted = false;
        console.log('[MultiplayerBattle] Бой окончен:', msg);
        if (onBattleEnd) {
          onBattleEnd({
            winner: msg.winner,
            reason: msg.reason
          });
        }
        break;

      case 'player_left':
        delete remotePlayers[msg.playerId];
        break;
    }
  }

  /**
   * Обновляет состояние игры
   */
  function updateGameState(data) {
    if (data.players) {
      const now = Date.now();
      
      // Сохраняем историю позиций для интерполяции
      for (const [id, p] of Object.entries(data.players)) {
        if (!playerHistory[id]) {
          playerHistory[id] = [];
        }
        playerHistory[id].push({ x: p.x, y: p.y, timestamp: now });
        // Храним только последние 5 позиций
        if (playerHistory[id].length > 5) {
          playerHistory[id].shift();
        }
      }
      
      // Интерполируем позиции (кроме локального игрока)
      const oldPlayers = { ...remotePlayers };
      remotePlayers = data.players;
      localPlayer = remotePlayers[playerId];
      
      // Для удалённых игроков интерполируем позиции
      for (const [id, p] of Object.entries(remotePlayers)) {
        if (id !== playerId && oldPlayers[id]) {
          const oldP = oldPlayers[id];
          if (oldP.x !== p.x || oldP.y !== p.y) {
            // Плавно интерполируем
            const history = playerHistory[id];
            if (history && history.length >= 2) {
              const targetTime = now - INTERPOLATION_DELAY;
              const target = history.find(h => h.timestamp >= targetTime) || history[history.length - 1];
              if (target) {
                p.x = target.x;
                p.y = target.y;
              }
            }
          }
        }
      }
      
      // Обновляем кнопку ульты
      if (ultButton && localPlayer) {
        const energyPercent = Math.floor(localPlayer.energy || 0);
        ultButton.innerHTML = `ULT<br>${energyPercent}%`;
        ultButton.disabled = (localPlayer.energy || 0) < 100;
        ultButton.style.opacity = (localPlayer.energy || 0) >= 100 ? '1' : '0.5';
      }
      
      // Принудительно обновляем все данные игроков для отображения HP
      // Это гарантирует, что HP бары обновляются в реальном времени
      if (data.players) {
        // Обновляем HP для всех игроков (включая локального)
        for (const [id, player] of Object.entries(data.players)) {
          if (player && player.hp !== undefined && player.maxHp !== undefined) {
            // Данные уже обновлены в updateGameState, просто убеждаемся что они актуальны
            const p = id === playerId ? localPlayer : remotePlayers[id];
            if (p) {
              p.hp = player.hp;
              p.maxHp = player.maxHp;
              p.energy = player.energy;
              p.isActive = player.isActive;
            }
          }
        }
      }
      
      // Обновляем перезарядку гаджета
      if (gadgetButton && localPlayer) {
        const heroData = getHeroData(localPlayer.hero);
        const cooldownTime = heroData?.gadget?.cooldown || 14000;
        
        if (localPlayer.lastGadget) {
          const timeSinceGadget = Date.now() - localPlayer.lastGadget;
          gadgetCooldown = Math.max(0, cooldownTime - timeSinceGadget);
        } else {
          gadgetCooldown = 0;
        }
        
        if (gadgetCooldown > 0) {
          const seconds = Math.ceil(gadgetCooldown / 1000);
          gadgetButton.innerHTML = `⚡<br>${seconds}s`;
          gadgetButton.disabled = true;
          gadgetButton.style.opacity = '0.5';
        } else {
          gadgetButton.innerHTML = '⚡<br>GADGET';
          gadgetButton.disabled = false;
          gadgetButton.style.opacity = '1';
        }
        
        // Логирование для отладки
        if (localPlayer.lastGadget) {
          console.log(`[MultiplayerBattle] Гаджет cooldown: ${gadgetCooldown}ms, lastGadget: ${localPlayer.lastGadget}, now: ${Date.now()}`);
        }
      }
    }
  }
  
  /**
   * Получает данные героя
   */
  function getHeroData(heroId) {
    const HEROES_DATA = {
      bitcor: { gadget: { cooldown: 14000 } },
      noizling: { gadget: { cooldown: 12000 } },
      forkbomb: { gadget: { cooldown: 15000 } },
      zalgoth: { gadget: { cooldown: 13000 } },
      errorborn: { gadget: { cooldown: 16000 } }
    };
    return HEROES_DATA[heroId] || HEROES_DATA.bitcor;
  }

  /**
   * Отправляет данные на сервер
   */
  function sendInput() {
    if (socket && socket.readyState === WebSocket.OPEN && localPlayer && battleStarted) {
      // Обновляем позицию локально для плавности
      const speed = localPlayer.speed || 3.0;
      const newX = Math.max(24, Math.min(MAP_WIDTH - 24, localPlayer.x + moveVector.x * speed * 2));
      const newY = Math.max(24, Math.min(MAP_HEIGHT - 24, localPlayer.y + moveVector.y * speed * 2));

      // Автоматическое наведение: используем угол движения
      // Сервер сам выберет ближайшего врага в радиусе для атаки
      let attackAngle = 0;
      if (moveVector.x !== 0 || moveVector.y !== 0) {
        // Если есть движение, используем угол движения
        attackAngle = Math.atan2(moveVector.y, moveVector.x);
      } else {
        // Если нет движения, используем последний угол или направление по умолчанию
        attackAngle = localPlayer.angle || 0;
      }
      
      socket.send(JSON.stringify({
        type: 'input',
        x: newX,
        y: newY,
        angle: attackAngle
      }));
    }
  }

  /**
   * Рендерит игру
   */
  function render() {
    if (!ctx || !canvas) return;

    ctx.clearRect(0, 0, VIEW_WIDTH, VIEW_HEIGHT);

    if (!localPlayer) {
      requestAnimationFrame(render);
      return;
    }

    // Камера следует за игроком (top-down вид с небольшим смещением для динамики)
    const cameraX = localPlayer.x - VIEW_WIDTH / 2;
    const cameraY = localPlayer.y - VIEW_HEIGHT / 2;

    ctx.save();
    ctx.translate(-cameraX, -cameraY);

    // Рисуем тематический фон арены (киберпанк/глитч стиль)
    // Основной фон с градиентом
    const bgGradient = ctx.createLinearGradient(0, 0, MAP_WIDTH, MAP_HEIGHT);
    bgGradient.addColorStop(0, '#0a0a1a');
    bgGradient.addColorStop(0.5, '#1a1a2e');
    bgGradient.addColorStop(1, '#0f0c29');
    ctx.fillStyle = bgGradient;
    ctx.fillRect(cameraX, cameraY, VIEW_WIDTH, VIEW_HEIGHT);
    
    // Рисуем сетку арены (глитч-стиль с неоновыми линиями)
    const gridSize = 100;
    const startX = Math.floor(cameraX / gridSize) * gridSize;
    const startY = Math.floor(cameraY / gridSize) * gridSize;
    const endX = cameraX + VIEW_WIDTH;
    const endY = cameraY + VIEW_HEIGHT;
    
    // Основные линии сетки (более яркие)
    ctx.strokeStyle = 'rgba(0, 200, 255, 0.15)';
    ctx.lineWidth = 1;
    for (let x = startX; x < endX; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, startY);
      ctx.lineTo(x, endY);
      ctx.stroke();
    }
    for (let y = startY; y < endY; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(startX, y);
      ctx.lineTo(endX, y);
      ctx.stroke();
    }
    
    // Центральные линии (более яркие для ориентации)
    const centerX = MAP_WIDTH / 2;
    const centerY = MAP_HEIGHT / 2;
    ctx.strokeStyle = 'rgba(255, 46, 0, 0.2)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(centerX, startY);
    ctx.lineTo(centerX, endY);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(startX, centerY);
    ctx.lineTo(endX, centerY);
    ctx.stroke();
    
    // Угловые маркеры (для ориентации)
    const cornerSize = 30;
    ctx.strokeStyle = 'rgba(0, 200, 255, 0.3)';
    ctx.lineWidth = 2;
    
    // Левый верхний угол
    if (cameraX < cornerSize && cameraY < cornerSize) {
      ctx.beginPath();
      ctx.moveTo(0, cornerSize);
      ctx.lineTo(0, 0);
      ctx.lineTo(cornerSize, 0);
      ctx.stroke();
    }
    
    // Правый верхний угол
    if (cameraX + VIEW_WIDTH > MAP_WIDTH - cornerSize && cameraY < cornerSize) {
      ctx.beginPath();
      ctx.moveTo(MAP_WIDTH, cornerSize);
      ctx.lineTo(MAP_WIDTH, 0);
      ctx.lineTo(MAP_WIDTH - cornerSize, 0);
      ctx.stroke();
    }
    
    // Левый нижний угол
    if (cameraX < cornerSize && cameraY + VIEW_HEIGHT > MAP_HEIGHT - cornerSize) {
      ctx.beginPath();
      ctx.moveTo(0, MAP_HEIGHT - cornerSize);
      ctx.lineTo(0, MAP_HEIGHT);
      ctx.lineTo(cornerSize, MAP_HEIGHT);
      ctx.stroke();
    }
    
    // Правый нижний угол
    if (cameraX + VIEW_WIDTH > MAP_WIDTH - cornerSize && cameraY + VIEW_HEIGHT > MAP_HEIGHT - cornerSize) {
      ctx.beginPath();
      ctx.moveTo(MAP_WIDTH, MAP_HEIGHT - cornerSize);
      ctx.lineTo(MAP_WIDTH, MAP_HEIGHT);
      ctx.lineTo(MAP_WIDTH - cornerSize, MAP_HEIGHT);
      ctx.stroke();
    }
    
    // Глитч-эффекты (случайные линии для атмосферы)
    const time = Date.now() / 1000;
    ctx.strokeStyle = 'rgba(255, 0, 255, 0.1)';
    ctx.lineWidth = 1;
    for (let i = 0; i < 3; i++) {
      const glitchX = (Math.sin(time + i) * 0.5 + 0.5) * MAP_WIDTH;
      const glitchY = (Math.cos(time * 0.7 + i) * 0.5 + 0.5) * MAP_HEIGHT;
      if (glitchX >= cameraX && glitchX <= cameraX + VIEW_WIDTH &&
          glitchY >= cameraY && glitchY <= cameraY + VIEW_HEIGHT) {
        ctx.beginPath();
        ctx.moveTo(glitchX - 20, glitchY);
        ctx.lineTo(glitchX + 20, glitchY);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(glitchX, glitchY - 20);
        ctx.lineTo(glitchX, glitchY + 20);
        ctx.stroke();
      }
    }
    
    // Рендерим препятствия (укрытия и стены)
    for (const obstacle of obstacles) {
      // Проверяем, находится ли препятствие в зоне видимости
      if (obstacle.x + obstacle.width < cameraX || 
          obstacle.x > cameraX + VIEW_WIDTH ||
          obstacle.y + obstacle.height < cameraY || 
          obstacle.y > cameraY + VIEW_HEIGHT) {
        continue; // Пропускаем препятствия вне зоны видимости
      }
      
      // Рисуем препятствие в глитч-стиле
      ctx.save();
      
      // Тень с размытием
      ctx.shadowBlur = 10;
      ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
      ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
      ctx.fillRect(obstacle.x + 3, obstacle.y + 3, obstacle.width, obstacle.height);
      ctx.shadowBlur = 0;
      
      // Основной цвет препятствия (киберпанк стиль)
      if (obstacle.type === 'cover') {
        // Укрытие - металлический градиент с неоновой обводкой
        const gradient = ctx.createLinearGradient(obstacle.x, obstacle.y, obstacle.x, obstacle.y + obstacle.height);
        gradient.addColorStop(0, '#2a2a3a');
        gradient.addColorStop(0.5, '#1a1a2a');
        gradient.addColorStop(1, '#0a0a1a');
        ctx.fillStyle = gradient;
      } else if (obstacle.isBoundary) {
        // Граничная стена - более яркая и заметная
        const boundaryGradient = ctx.createLinearGradient(obstacle.x, obstacle.y, obstacle.x + obstacle.width, obstacle.y + obstacle.height);
        boundaryGradient.addColorStop(0, '#1a1a3a');
        boundaryGradient.addColorStop(0.5, '#0a0a2a');
        boundaryGradient.addColorStop(1, '#0a0a1a');
        ctx.fillStyle = boundaryGradient;
      } else {
        // Обычная стена - тёмный металл
        const wallGradient = ctx.createLinearGradient(obstacle.x, obstacle.y, obstacle.x + obstacle.width, obstacle.y);
        wallGradient.addColorStop(0, '#1a1a2a');
        wallGradient.addColorStop(1, '#0a0a1a');
        ctx.fillStyle = wallGradient;
      }
      
      // Скруглённые углы (эффект)
      const cornerRadius = 4;
      ctx.beginPath();
      ctx.moveTo(obstacle.x + cornerRadius, obstacle.y);
      ctx.lineTo(obstacle.x + obstacle.width - cornerRadius, obstacle.y);
      ctx.quadraticCurveTo(obstacle.x + obstacle.width, obstacle.y, obstacle.x + obstacle.width, obstacle.y + cornerRadius);
      ctx.lineTo(obstacle.x + obstacle.width, obstacle.y + obstacle.height - cornerRadius);
      ctx.quadraticCurveTo(obstacle.x + obstacle.width, obstacle.y + obstacle.height, obstacle.x + obstacle.width - cornerRadius, obstacle.y + obstacle.height);
      ctx.lineTo(obstacle.x + cornerRadius, obstacle.y + obstacle.height);
      ctx.quadraticCurveTo(obstacle.x, obstacle.y + obstacle.height, obstacle.x, obstacle.y + obstacle.height - cornerRadius);
      ctx.lineTo(obstacle.x, obstacle.y + cornerRadius);
      ctx.quadraticCurveTo(obstacle.x, obstacle.y, obstacle.x + cornerRadius, obstacle.y);
      ctx.closePath();
      ctx.fill();
      
      // Неоновая обводка (киберпанк стиль)
      if (obstacle.isBoundary) {
        // Граничные стены - яркая красная обводка для предупреждения
        ctx.strokeStyle = 'rgba(255, 0, 0, 0.8)';
        ctx.lineWidth = 3;
        ctx.shadowBlur = 10;
        ctx.shadowColor = 'rgba(255, 0, 0, 0.6)';
      } else {
        ctx.strokeStyle = obstacle.type === 'cover' ? 'rgba(0, 200, 255, 0.6)' : 'rgba(255, 46, 0, 0.5)';
        ctx.lineWidth = 2;
        ctx.shadowBlur = 5;
        ctx.shadowColor = obstacle.type === 'cover' ? 'rgba(0, 200, 255, 0.5)' : 'rgba(255, 46, 0, 0.5)';
      }
      ctx.stroke();
      ctx.shadowBlur = 0;
      
      // Внутренняя детализация (технологический паттерн)
      if (obstacle.type === 'cover') {
        ctx.strokeStyle = 'rgba(0, 200, 255, 0.2)';
        ctx.lineWidth = 1;
        // Горизонтальные линии
        for (let i = 1; i < 3; i++) {
          const lineY = obstacle.y + (obstacle.height / 3) * i;
          ctx.beginPath();
          ctx.moveTo(obstacle.x + 5, lineY);
          ctx.lineTo(obstacle.x + obstacle.width - 5, lineY);
          ctx.stroke();
        }
        // Вертикальные линии
        ctx.beginPath();
        ctx.moveTo(obstacle.x + obstacle.width / 2, obstacle.y + 5);
        ctx.lineTo(obstacle.x + obstacle.width / 2, obstacle.y + obstacle.height - 5);
        ctx.stroke();
        
        // Угловые маркеры
        ctx.fillStyle = 'rgba(0, 200, 255, 0.4)';
        const markerSize = 3;
        ctx.fillRect(obstacle.x + 5, obstacle.y + 5, markerSize, markerSize);
        ctx.fillRect(obstacle.x + obstacle.width - 8, obstacle.y + 5, markerSize, markerSize);
        ctx.fillRect(obstacle.x + 5, obstacle.y + obstacle.height - 8, markerSize, markerSize);
        ctx.fillRect(obstacle.x + obstacle.width - 8, obstacle.y + obstacle.height - 8, markerSize, markerSize);
      }
      
      ctx.restore();
    }
    
    // Рендерим электрическое облако (сжимающаяся зона)
    if (storm && storm.active) {
      ctx.save();
      
      const stormX = storm.centerX;
      const stormY = storm.centerY;
      const stormRadius = storm.currentRadius;
      
      // Проверяем, находится ли облако в зоне видимости
      const stormVisible = !(stormX + stormRadius < cameraX || 
                             stormX - stormRadius > cameraX + VIEW_WIDTH ||
                             stormY + stormRadius < cameraY || 
                             stormY - stormRadius > cameraY + VIEW_HEIGHT);
      
      if (stormVisible) {
        // Создаём градиент для электрического эффекта
        const gradient = ctx.createRadialGradient(stormX, stormY, 0, stormX, stormY, stormRadius);
        gradient.addColorStop(0, 'rgba(255, 255, 0, 0.0)'); // Прозрачный центр
        gradient.addColorStop(0.7, 'rgba(255, 200, 0, 0.3)'); // Жёлтый
        gradient.addColorStop(0.9, 'rgba(255, 100, 0, 0.5)'); // Оранжевый
        gradient.addColorStop(1, 'rgba(255, 0, 0, 0.7)'); // Красный на краю
        
        // Рисуем внешнюю опасную зону (красная)
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(stormX, stormY, Math.max(MAP_WIDTH, MAP_HEIGHT) * 0.9, 0, Math.PI * 2);
        ctx.arc(stormX, stormY, stormRadius, 0, Math.PI * 2, true); // Вычитаем безопасную зону
        ctx.fill();
        
        // Рисуем границу безопасной зоны (электрический эффект)
        ctx.strokeStyle = 'rgba(255, 255, 0, 0.8)';
        ctx.lineWidth = 4;
        ctx.shadowBlur = 20;
        ctx.shadowColor = 'rgba(255, 255, 0, 0.8)';
        ctx.beginPath();
        ctx.arc(stormX, stormY, stormRadius, 0, Math.PI * 2);
        ctx.stroke();
        
        // Электрические разряды на границе (визуальный эффект)
        const time = Date.now() / 100;
        for (let i = 0; i < 8; i++) {
          const angle = (i / 8) * Math.PI * 2 + time;
          const sparkX = stormX + Math.cos(angle) * stormRadius;
          const sparkY = stormY + Math.sin(angle) * stormRadius;
          const sparkLength = 15 + Math.sin(time * 2 + i) * 5;
          
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
          ctx.lineWidth = 2;
          ctx.shadowBlur = 10;
          ctx.shadowColor = 'rgba(255, 255, 0, 1)';
          ctx.beginPath();
          ctx.moveTo(sparkX, sparkY);
          ctx.lineTo(
            sparkX + Math.cos(angle) * sparkLength,
            sparkY + Math.sin(angle) * sparkLength
          );
          ctx.stroke();
        }
        
        ctx.shadowBlur = 0;
      }
      
      ctx.restore();
    }

    // Рендерим всех игроков
    for (const p of Object.values(remotePlayers)) {
      if (!p || !p.isActive || p.hp <= 0) continue;

      const isLocal = p.id === playerId;
      const color = TEAM_COLORS[p.team] || '#ffffff';

      // Тень под игроком (для объёма)
      ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
      ctx.beginPath();
      ctx.ellipse(p.x, p.y + 15, 20, 10, 0, 0, Math.PI * 2);
      ctx.fill();

      // Эмодзи героя (увеличенный размер)
      ctx.font = '40px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = color;
      ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
      ctx.shadowBlur = 4;
      ctx.fillText(EMOJIS[p.hero] || '❓', p.x, p.y);
      ctx.shadowBlur = 0;

      // HP бар
      const barWidth = 70;
      const barHeight = 10;
      const ratio = p.hp / p.maxHp;

      ctx.fillStyle = '#333';
      ctx.fillRect(p.x - barWidth / 2, p.y - 55, barWidth, barHeight);

      ctx.fillStyle = ratio > 0.5 ? '#4dff4d' : ratio > 0.25 ? '#ffcc00' : '#ff4d4d';
      ctx.fillRect(p.x - barWidth / 2, p.y - 55, barWidth * ratio, barHeight);

      // Имя игрока (только для локального)
      if (isLocal) {
        ctx.font = 'bold 14px Arial';
        ctx.fillStyle = '#ffffff';
        ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
        ctx.shadowBlur = 2;
        ctx.fillText('YOU', p.x, p.y - 70);
        ctx.shadowBlur = 0;
      }

      // Энергия (только для локального)
      if (isLocal && p.energy > 0) {
        ctx.fillStyle = '#00C8FF';
        ctx.fillRect(p.x - barWidth / 2, p.y - 45, barWidth * (p.energy / 100), 6);
      }
      
      // Направление атаки (линия прицеливания для локального игрока)
      if (isLocal && aimVector.x !== 0 && aimVector.y !== 0) {
        ctx.strokeStyle = 'rgba(255, 46, 0, 0.5)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(p.x + aimVector.x * 50, p.y + aimVector.y * 50);
        ctx.stroke();
      }
      
      // Статус-эффекты (визуальные индикаторы)
      if (p.statusEffects && p.statusEffects.length > 0) {
        const now = Date.now();
        const activeEffects = p.statusEffects.filter(e => !e.expiresAt || e.expiresAt > now);
        if (activeEffects.length > 0) {
          ctx.font = '10px Arial';
          ctx.textAlign = 'center';
          ctx.fillStyle = '#ffff00';
          let offsetY = -80;
          for (const effect of activeEffects) {
            let icon = '⚡';
            if (effect.type === 'invulnerable') icon = '🛡️';
            else if (effect.type === 'sacrifice_buff') icon = '⚡';
            else if (effect.type === 'stunned') icon = '💫';
            else if (effect.type === 'confused') icon = '🌀';
            ctx.fillText(icon, p.x, p.y + offsetY);
            offsetY -= 12;
          }
        }
      }
    }

    // Рендерим снаряды
    for (const projectile of projectiles) {
      ctx.save();
      const teamColor = TEAM_COLORS[projectile.team] || '#ffffff';
      ctx.fillStyle = teamColor;
      ctx.shadowColor = teamColor;
      ctx.shadowBlur = 8;
      ctx.beginPath();
      ctx.arc(projectile.x, projectile.y, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;
      
      // След снаряда
      ctx.strokeStyle = teamColor;
      ctx.lineWidth = 2;
      ctx.globalAlpha = 0.5;
      ctx.beginPath();
      ctx.moveTo(projectile.x - projectile.vx * 3, projectile.y - projectile.vy * 3);
      ctx.lineTo(projectile.x, projectile.y);
      ctx.stroke();
      ctx.globalAlpha = 1.0;
      ctx.restore();
    }
    
    // Рендерим визуальные эффекты (проектили, вспышки)
    const now = Date.now();
    visualEffects = visualEffects.filter(effect => {
      const progress = (now - effect.startTime) / effect.maxLifetime;
      if (progress >= 1) return false;
      
      // Рендерим эффект
      ctx.save();
      ctx.globalAlpha = 1 - progress;
      
      if (effect.type === 'projectile') {
        // Проектиль для дальнобойных атак
        const x = effect.startX + (effect.endX - effect.startX) * progress;
        const y = effect.startY + (effect.endY - effect.startY) * progress;
        ctx.fillStyle = effect.color || '#ff4d4d';
        ctx.beginPath();
        ctx.arc(x, y, 8, 0, Math.PI * 2);
        ctx.fill();
        // След
        ctx.strokeStyle = effect.color || '#ff4d4d';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(effect.startX, effect.startY);
        ctx.lineTo(x, y);
        ctx.stroke();
      } else if (effect.type === 'explosion') {
        // Вспышка для ближнего боя
        const x = effect.x;
        const y = effect.y;
        const radius = effect.radius * (1 - progress);
        const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
        gradient.addColorStop(0, effect.color || '#ff4d4d');
        gradient.addColorStop(1, 'transparent');
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fill();
      }
      
      ctx.restore();
      return true;
    });
    
    // Рендерим числа урона
    damageNumbers = damageNumbers.filter(dmg => {
      const progress = (now - dmg.startTime) / dmg.lifetime;
      if (progress >= 1) return false;
      
      ctx.save();
      ctx.font = `bold ${16 + progress * 8}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.globalAlpha = 1 - progress;
      ctx.fillStyle = dmg.color || '#ff4d4d';
      ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
      ctx.shadowBlur = 2;
      
      const offsetX = (progress - 0.5) * 30;
      const offsetY = -progress * 40;
      ctx.fillText(`-${dmg.damage}`, dmg.x + offsetX, dmg.y + offsetY);
      
      ctx.restore();
      return true;
    });

    ctx.restore();
    requestAnimationFrame(render);
  }
  
  /**
   * Создаёт визуальный эффект урона от электрического облака
   */
  function createStormDamageEffect(x, y, damage) {
    const now = Date.now();
    
    // Электрическая вспышка
    visualEffects.push({
      type: 'explosion',
      x: x,
      y: y,
      radius: 40,
      startTime: now,
      maxLifetime: 300,
      color: '#ffff00' // Жёлтый цвет для электричества
    });
    
    // Число урона (жёлтый цвет для электрического урона)
    damageNumbers.push({
      x: x,
      y: y,
      damage: damage,
      startTime: now,
      lifetime: 1200,
      color: '#ffff00' // Жёлтый цвет
    });
  }
  
  /**
   * Создаёт визуальный эффект атаки
   */
  function createAttackEffect(attacker, target, damage) {
    const now = Date.now();
    const isRanged = ['noizling', 'zalgoth', 'errorborn'].includes(attacker.hero);
    
    if (isRanged) {
      // Проектиль для дальнобойных
      visualEffects.push({
        type: 'projectile',
        startX: attacker.x,
        startY: attacker.y,
        endX: target.x,
        endY: target.y,
        startTime: now,
        maxLifetime: 300,
        color: '#ff4d4d'
      });
    } else {
      // Вспышка для ближнего боя
      visualEffects.push({
        type: 'explosion',
        x: target.x,
        y: target.y,
        radius: 30,
        startTime: now,
        maxLifetime: 200,
        color: '#ff4d4d'
      });
    }
    
    // Число урона
    damageNumbers.push({
      x: target.x,
      y: target.y,
      damage: damage,
      startTime: now,
      lifetime: 1000,
      color: damage > 100 ? '#ff0000' : '#ff4d4d'
    });
  }

  /**
   * Создаёт загрузочный экран
   */
  function createLoadingScreen(hero) {
    loadingScreen = document.createElement('div');
    loadingScreen.id = 'multiplayer-loading-screen';
    loadingScreen.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: linear-gradient(135deg, #0a0a1a 0%, #1a1a2e 50%, #0f0c29 100%);
      z-index: 2000;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: white;
      font-family: Arial, sans-serif;
    `;
    
    // Логотип Glitchlings
    const logo = document.createElement('div');
    logo.style.cssText = `
      font-size: 48px;
      font-weight: bold;
      margin-bottom: 40px;
      text-shadow: 0 0 20px rgba(255, 46, 0, 0.8);
      color: #FF2E00;
    `;
    logo.textContent = 'GLITCHLINGS';
    loadingScreen.appendChild(logo);
    
    // Эмодзи героя
    const heroEmoji = document.createElement('div');
    heroEmoji.style.cssText = `
      font-size: 80px;
      margin-bottom: 20px;
      animation: pulse 1.5s ease-in-out infinite;
    `;
    heroEmoji.textContent = EMOJIS[hero] || '🦉';
    loadingScreen.appendChild(heroEmoji);
    
    // Текст загрузки
    const loadingText = document.createElement('div');
    loadingText.id = 'loading-text';
    loadingText.style.cssText = `
      font-size: 24px;
      margin-bottom: 30px;
      color: #00C8FF;
      text-shadow: 0 0 10px rgba(0, 200, 255, 0.5);
    `;
    loadingText.textContent = 'Поиск противников...';
    loadingScreen.appendChild(loadingText);
    
    // Кнопка отмены поиска
    const cancelButton = document.createElement('button');
    cancelButton.id = 'cancel-search-btn';
    cancelButton.style.cssText = `
      margin-top: 30px;
      padding: 12px 32px;
      background: rgba(255, 46, 0, 0.2);
      border: 2px solid #FF2E00;
      border-radius: 8px;
      color: #FF2E00;
      font-size: 18px;
      font-weight: bold;
      cursor: pointer;
      transition: all 0.3s ease;
      pointer-events: auto;
    `;
    cancelButton.textContent = '❌ Отменить поиск';
    cancelButton.onmouseover = () => {
      cancelButton.style.background = 'rgba(255, 46, 0, 0.3)';
      cancelButton.style.transform = 'scale(1.05)';
    };
    cancelButton.onmouseout = () => {
      cancelButton.style.background = 'rgba(255, 46, 0, 0.2)';
      cancelButton.style.transform = 'scale(1)';
    };
    cancelButton.onclick = () => {
      searchCancelled = true;
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'cancel_search' }));
      }
      if (socket) {
        socket.close(1000, 'Search cancelled by user');
      }
      if (onBattleEnd) {
        onBattleEnd({ reason: 'player_cancelled', winner: null });
      }
    };
    loadingScreen.appendChild(cancelButton);
    
    // Прогресс-бар контейнер
    const progressContainer = document.createElement('div');
    progressContainer.style.cssText = `
      width: 80%;
      max-width: 400px;
      height: 8px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 4px;
      overflow: hidden;
      margin-bottom: 20px;
    `;
    
    const progressBar = document.createElement('div');
    progressBar.id = 'loading-progress-bar';
    progressBar.style.cssText = `
      height: 100%;
      width: 0%;
      background: linear-gradient(90deg, #FF2E00 0%, #ff4d4d 50%, #FF2E00 100%);
      border-radius: 4px;
      transition: width 0.3s ease;
      box-shadow: 0 0 10px rgba(255, 46, 0, 0.6);
    `;
    progressContainer.appendChild(progressBar);
    loadingScreen.appendChild(progressContainer);
    
    // Процент загрузки
    const progressPercent = document.createElement('div');
    progressPercent.id = 'loading-percent';
    progressPercent.style.cssText = `
      font-size: 18px;
      color: #ffffff;
      text-shadow: 0 0 5px rgba(0, 0, 0, 0.8);
    `;
    progressPercent.textContent = '0%';
    loadingScreen.appendChild(progressPercent);
    
    // Добавляем анимацию пульсации
    if (!document.getElementById('loading-pulse-style')) {
      const style = document.createElement('style');
      style.id = 'loading-pulse-style';
      style.textContent = `
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
      `;
      document.head.appendChild(style);
    }
    
    container.appendChild(loadingScreen);
    
    // Симуляция прогресса загрузки (только до 60%, остальное - реальный поиск)
    loadingProgress = 0;
    const progressInterval = setInterval(() => {
      if (searchCancelled) {
        clearInterval(progressInterval);
        return;
      }
      if (loadingProgress < 60) {
        loadingProgress += Math.random() * 3;
        loadingProgress = Math.min(loadingProgress, 60);
        updateLoadingProgress(loadingProgress);
      }
    }, 300);
    
    // Сохраняем интервал для очистки
    loadingScreen._progressInterval = progressInterval;
    
    // Таймаут поиска (60 секунд)
    loadingScreen._searchTimeout = setTimeout(() => {
      if (!searchCancelled && loadingScreen) {
        updateLoadingText('Поиск занимает больше времени...');
      }
    }, 60000);
  }
  
  /**
   * Обновляет прогресс загрузки
   */
  function updateLoadingProgress(percent) {
    loadingProgress = percent;
    const progressBar = document.getElementById('loading-progress-bar');
    const progressPercent = document.getElementById('loading-percent');
    const loadingText = document.getElementById('loading-text');
    
    if (progressBar) {
      progressBar.style.width = `${percent}%`;
    }
    if (progressPercent) {
      progressPercent.textContent = `${Math.floor(percent)}%`;
    }
    if (loadingText) {
      if (percent < 30) {
        loadingText.textContent = 'Поиск противников...';
      } else if (percent < 60) {
        loadingText.textContent = 'Подключение к серверу...';
      } else if (percent < 90) {
        loadingText.textContent = 'Создание комнаты...';
      } else {
        loadingText.textContent = 'Готово к бою!';
      }
    }
  }
  
  /**
   * Обновляет текст загрузки
   */
  function updateLoadingText(text) {
    const loadingText = document.getElementById('loading-text');
    if (loadingText) {
      loadingText.textContent = text;
    }
  }
  
  /**
   * Показывает экран подтверждения готовности
   */
  function showReadyScreen(matchData) {
    if (readyScreen) {
      hideReadyScreen();
    }
    
    readyScreen = document.createElement('div');
    readyScreen.id = 'ready-screen';
    readyScreen.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: linear-gradient(135deg, #0a0a1a 0%, #1a1a2e 50%, #0f0c29 100%);
      z-index: 2000;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: white;
      font-family: Arial, sans-serif;
    `;
    
    // Заголовок
    const title = document.createElement('div');
    title.style.cssText = `
      font-size: 36px;
      font-weight: bold;
      margin-bottom: 20px;
      text-shadow: 0 0 20px rgba(255, 46, 0, 0.8);
      color: #FF2E00;
    `;
    title.textContent = 'МАТЧ НАЙДЕН!';
    readyScreen.appendChild(title);
    
    // Список игроков
    const playersList = document.createElement('div');
    playersList.id = 'ready-players-list';
    playersList.style.cssText = `
      margin-bottom: 30px;
      text-align: center;
      font-size: 18px;
    `;
    readyScreen.appendChild(playersList);
    
    // Статус готовности
    const readyStatus = document.createElement('div');
    readyStatus.id = 'ready-status';
    readyStatus.style.cssText = `
      font-size: 24px;
      margin-bottom: 30px;
      color: #00C8FF;
      text-shadow: 0 0 10px rgba(0, 200, 255, 0.5);
    `;
    readyStatus.textContent = 'Ожидание подтверждения...';
    readyScreen.appendChild(readyStatus);
    
    // Кнопка готовности
    const readyButton = document.createElement('button');
    readyButton.id = 'ready-btn';
    readyButton.style.cssText = `
      padding: 16px 48px;
      background: linear-gradient(135deg, #00C8FF, #0088cc);
      border: none;
      border-radius: 12px;
      color: white;
      font-size: 24px;
      font-weight: bold;
      cursor: pointer;
      box-shadow: 0 4px 20px rgba(0, 200, 255, 0.4);
      transition: all 0.3s ease;
      pointer-events: auto;
    `;
    readyButton.textContent = '✅ ГОТОВ К БОЮ';
    readyButton.onmouseover = () => {
      readyButton.style.transform = 'scale(1.05)';
      readyButton.style.boxShadow = '0 6px 25px rgba(0, 200, 255, 0.6)';
    };
    readyButton.onmouseout = () => {
      readyButton.style.transform = 'scale(1)';
      readyButton.style.boxShadow = '0 4px 20px rgba(0, 200, 255, 0.4)';
    };
    readyButton.onclick = () => {
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'ready' }));
        readyButton.disabled = true;
        readyButton.textContent = '⏳ Ожидание других игроков...';
        readyButton.style.opacity = '0.7';
      }
    };
    readyScreen.appendChild(readyButton);
    
    // Обновляем список игроков
    updateReadyPlayersList(matchData.players || []);
    updateReadyStatus(0, matchData.players?.length || 0);
    
    container.appendChild(readyScreen);
  }
  
  /**
   * Обновляет список игроков на экране готовности
   */
  function updateReadyPlayersList(players) {
    const playersList = document.getElementById('ready-players-list');
    if (!playersList) return;
    
    playersList.innerHTML = players.map(p => {
      const emoji = EMOJIS[p.hero] || '❓';
      const teamColor = p.team === 'blue' ? '#4dfffd' : '#ff6666';
      return `<div style="margin: 8px 0; color: ${teamColor};">${emoji} ${p.team === 'blue' ? '🔵' : '🔴'} Игрок ${p.id.substring(0, 6)}</div>`;
    }).join('');
  }
  
  /**
   * Обновляет статус готовности
   */
  function updateReadyStatus(readyCount, totalCount) {
    const readyStatus = document.getElementById('ready-status');
    if (!readyStatus) return;
    
    if (readyCount === totalCount) {
      readyStatus.textContent = '✅ Все игроки готовы!';
      readyStatus.style.color = '#4dff4d';
    } else {
      readyStatus.textContent = `Готово: ${readyCount}/${totalCount}`;
      readyStatus.style.color = '#00C8FF';
    }
  }
  
  /**
   * Скрывает экран подтверждения готовности
   */
  function hideReadyScreen() {
    if (readyScreen) {
      readyScreen.style.opacity = '0';
      readyScreen.style.transition = 'opacity 0.3s ease';
      setTimeout(() => {
        if (readyScreen && readyScreen.parentNode) {
          readyScreen.remove();
        }
        readyScreen = null;
      }, 300);
    }
  }
  
  /**
   * Скрывает загрузочный экран
   */
  /**
   * Показывает экран выбора режима игры (PvP/PvE)
   */
  function showGameModeScreen() {
    console.log('[MultiplayerBattle] showGameModeScreen вызвана');
    if (gameModeScreen) {
      console.log('[MultiplayerBattle] Экран выбора режима уже существует');
      return; // Уже показан
    }
    
    if (!container) {
      console.error('[MultiplayerBattle] ❌ Контейнер не найден!');
      return;
    }
    
    console.log('[MultiplayerBattle] Создаём экран выбора режима...');
    gameModeScreen = document.createElement('div');
    gameModeScreen.id = 'game-mode-screen';
    gameModeScreen.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: linear-gradient(135deg, #0a0a1a 0%, #1a1a2e 50%, #0f0c29 100%);
      z-index: 2000;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: white;
      font-family: Arial, sans-serif;
    `;
    
    // Логотип
    const logo = document.createElement('div');
    logo.style.cssText = `
      font-size: 48px;
      font-weight: bold;
      margin-bottom: 20px;
      text-shadow: 0 0 20px rgba(255, 46, 0, 0.8);
      color: #FF2E00;
    `;
    logo.textContent = 'GLITCHLINGS';
    gameModeScreen.appendChild(logo);
    
    // Заголовок
    const title = document.createElement('div');
    title.style.cssText = `
      font-size: 32px;
      margin-bottom: 50px;
      color: #00C8FF;
      text-shadow: 0 0 10px rgba(0, 200, 255, 0.5);
    `;
    title.textContent = 'Выберите режим игры';
    gameModeScreen.appendChild(title);
    
    // Контейнер для кнопок режимов
    const modesContainer = document.createElement('div');
    modesContainer.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 20px;
      width: 90%;
      max-width: 400px;
    `;
    
    // Кнопка PvE (игроки + боты)
    const pveButton = document.createElement('button');
    pveButton.style.cssText = `
      padding: 20px 40px;
      background: linear-gradient(135deg, #00C8FF 0%, #0088cc 100%);
      border: 3px solid #00C8FF;
      border-radius: 12px;
      color: white;
      font-size: 24px;
      font-weight: bold;
      cursor: pointer;
      transition: all 0.3s ease;
      box-shadow: 0 4px 15px rgba(0, 200, 255, 0.4);
    `;
    pveButton.innerHTML = '🤖 PvE<br><span style="font-size: 16px; opacity: 0.9;">Игроки + Боты</span>';
    pveButton.onmouseover = () => {
      pveButton.style.transform = 'scale(1.05)';
      pveButton.style.boxShadow = '0 6px 20px rgba(0, 200, 255, 0.6)';
    };
    pveButton.onmouseout = () => {
      pveButton.style.transform = 'scale(1)';
      pveButton.style.boxShadow = '0 4px 15px rgba(0, 200, 255, 0.4)';
    };
    pveButton.onclick = () => {
      selectedGameMode = 'pve';
      startSearch('pve');
    };
    modesContainer.appendChild(pveButton);
    
    // Кнопка PvP (только игроки)
    const pvpButton = document.createElement('button');
    pvpButton.style.cssText = `
      padding: 20px 40px;
      background: linear-gradient(135deg, #ff4d4d 0%, #cc0000 100%);
      border: 3px solid #ff4d4d;
      border-radius: 12px;
      color: white;
      font-size: 24px;
      font-weight: bold;
      cursor: pointer;
      transition: all 0.3s ease;
      box-shadow: 0 4px 15px rgba(255, 77, 77, 0.4);
    `;
    pvpButton.innerHTML = '⚔️ PvP<br><span style="font-size: 16px; opacity: 0.9;">Только игроки</span>';
    pvpButton.onmouseover = () => {
      pvpButton.style.transform = 'scale(1.05)';
      pvpButton.style.boxShadow = '0 6px 20px rgba(255, 77, 77, 0.6)';
    };
    pvpButton.onmouseout = () => {
      pvpButton.style.transform = 'scale(1)';
      pvpButton.style.boxShadow = '0 4px 15px rgba(255, 77, 77, 0.4)';
    };
    pvpButton.onclick = () => {
      selectedGameMode = 'pvp';
      startSearch('pvp');
    };
    modesContainer.appendChild(pvpButton);
    
    gameModeScreen.appendChild(modesContainer);
    
    // Добавляем экран прямо в body, чтобы он точно был виден
    // Используем body вместо container, так как container может быть скрыт
    const body = document.body;
    if (body) {
      body.appendChild(gameModeScreen);
      console.log('[MultiplayerBattle] ✅ Экран выбора режима добавлен в body');
      console.log('[MultiplayerBattle] Размеры экрана выбора режима:', gameModeScreen.offsetWidth, 'x', gameModeScreen.offsetHeight);
      console.log('[MultiplayerBattle] Стили экрана:', window.getComputedStyle(gameModeScreen).display, window.getComputedStyle(gameModeScreen).zIndex);
    } else {
      // Fallback: добавляем в контейнер, если body недоступен
      if (container) {
        container.style.display = 'block';
        container.style.visibility = 'visible';
        container.appendChild(gameModeScreen);
        console.log('[MultiplayerBattle] ✅ Экран выбора режима добавлен в контейнер (fallback)');
      } else {
        console.error('[MultiplayerBattle] ❌ Не удалось найти body или container!');
      }
    }
  }
  
  /**
   * Скрывает экран выбора режима игры
   */
  function hideGameModeScreen() {
    if (gameModeScreen) {
      console.log('[MultiplayerBattle] Скрываем экран выбора режима');
      gameModeScreen.style.opacity = '0';
      gameModeScreen.style.transition = 'opacity 0.3s ease';
      setTimeout(() => {
        if (gameModeScreen && gameModeScreen.parentNode) {
          gameModeScreen.remove();
        }
        gameModeScreen = null;
        console.log('[MultiplayerBattle] Экран выбора режима удалён');
      }, 300);
    }
  }
  
  /**
   * Начинает поиск матча с выбранным режимом
   */
  function startSearch(gameMode) {
    if (socket && socket.readyState === WebSocket.OPEN) {
      console.log(`[MultiplayerBattle] Начинаем поиск в режиме: ${gameMode}`);
      socket.send(JSON.stringify({
        type: 'start_search',
        gameMode: gameMode
      }));
    } else {
      console.error('[MultiplayerBattle] WebSocket не подключен');
    }
  }
  
  function hideLoadingScreen() {
    if (loadingScreen) {
      // Очищаем интервал
      if (loadingScreen._progressInterval) {
        clearInterval(loadingScreen._progressInterval);
      }
      
      // Очищаем таймаут поиска
      if (loadingScreen._searchTimeout) {
        clearTimeout(loadingScreen._searchTimeout);
      }
      
      // Плавно скрываем экран
      setTimeout(() => {
        if (loadingScreen && loadingScreen.parentNode) {
          loadingScreen.style.opacity = '0';
          loadingScreen.style.transition = 'opacity 0.5s ease';
          setTimeout(() => {
            if (loadingScreen && loadingScreen.parentNode) {
              loadingScreen.remove();
            }
            loadingScreen = null;
          }, 500);
        }
      }, 300);
    }
  }

  /**
   * Запускает мультиплеерный бой
   */
  function startBattle(hero) {
    // Сохраняем выбранного героя
    currentHero = hero;
    searchCancelled = false;
    
    // Очищаем контейнер
    console.log('[MultiplayerBattle] Очищаем контейнер и настраиваем стили');
    container.innerHTML = '';
    container.style.cssText = `
      position: relative;
      width: 100%;
      height: 100vh;
      overflow: hidden;
      background: #0a0a1a;
    `;

    // НЕ показываем загрузочный экран сразу - покажем после подключения и выбора режима
    // Экран выбора режима появится после получения сообщения 'connected'

    // Инициализируем canvas и UI (скрыты до начала боя)
    initCanvas();
    createUI();
    
    // Скрываем canvas и UI до начала боя
    if (canvas) {
      canvas.style.display = 'none';
    }
    const uiContainer = document.getElementById('multiplayer-ui');
    if (uiContainer) {
      uiContainer.style.display = 'none';
    }
    if (ultButton) {
      ultButton.style.display = 'none';
    }
    if (gadgetButton) {
      gadgetButton.style.display = 'none';
    }
    const attackBtn = document.getElementById('attack-btn');
    if (attackBtn) {
      attackBtn.style.display = 'none';
    }

    // Подключаемся к серверу
    connectToServer(hero);

    // Запускаем рендер (но он скрыт)
    render();

    // Отправляем input каждые 50 мс
    const inputInterval = setInterval(() => {
      if (battleStarted) {
        sendInput();
      }
    }, 50);
    
    // Сохраняем интервалы для очистки
    cleanup.intervals.push(inputInterval);
    window.__multiplayerInputInterval = inputInterval;
  }

  /**
   * Останавливает бой и очищает все ресурсы (предотвращение утечек памяти)
   */
  function stopBattle() {
    console.log('[MultiplayerBattle] Остановка боя и очистка ресурсов...');
    
    // Останавливаем бой
    battleStarted = false;
    
    // Закрываем WebSocket и очищаем обработчики
    if (socket) {
      // Удаляем обработчики перед закрытием
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      
      if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
        socket.close();
      }
      socket = null;
    }
    
    // Очищаем обработчик resize
    if (cleanup.resizeHandler) {
      window.removeEventListener('resize', cleanup.resizeHandler);
      cleanup.resizeHandler = null;
    }
    
    // Очищаем обработчики джойстиков
    cleanup.joystickHandlers.forEach(({ joystick, handleStart, moveHandler, endHandler }) => {
      if (joystick) {
        joystick.removeEventListener('mousedown', handleStart);
        joystick.removeEventListener('touchstart', handleStart);
      }
      if (moveHandler) {
        document.removeEventListener('mousemove', moveHandler);
        document.removeEventListener('touchmove', moveHandler);
      }
      if (endHandler) {
        document.removeEventListener('mouseup', endHandler);
        document.removeEventListener('touchend', endHandler);
        document.removeEventListener('touchcancel', endHandler);
      }
    });
    cleanup.joystickHandlers = [];
    
    // Очищаем обработчики кнопок
    cleanup.buttonHandlers.forEach(({ element, type, handler }) => {
      if (element && handler) {
        element.removeEventListener(type, handler);
      }
    });
    cleanup.buttonHandlers = [];
    
    // Очищаем все таймеры
    cleanup.intervals.forEach(intervalId => {
      if (intervalId) clearInterval(intervalId);
    });
    cleanup.intervals = [];
    
    cleanup.timeouts.forEach(timeoutId => {
      if (timeoutId) clearTimeout(timeoutId);
    });
    cleanup.timeouts = [];
    
    // Очищаем глобальный интервал
    if (window.__multiplayerInputInterval) {
      clearInterval(window.__multiplayerInputInterval);
      window.__multiplayerInputInterval = null;
    }
    
    // Очищаем Canvas
    if (ctx) {
      ctx.clearRect(0, 0, canvas?.width || 0, canvas?.height || 0);
      ctx = null;
    }
    if (canvas) {
      canvas.remove();
      canvas = null;
    }
    
    // Очищаем UI элементы
    const uiContainer = document.getElementById('multiplayer-ui');
    if (uiContainer) {
      uiContainer.remove();
    }
    if (ultButton) {
      ultButton.remove();
      ultButton = null;
    }
    if (gadgetButton) {
      gadgetButton.remove();
      gadgetButton = null;
    }
    const attackBtn = document.getElementById('attack-btn');
    if (attackBtn) {
      attackBtn.remove();
    }
    
    // Очищаем экран выбора режима
    if (gameModeScreen) {
      if (gameModeScreen.parentNode) {
        gameModeScreen.remove();
      }
      gameModeScreen = null;
    }
    
    // Очищаем загрузочный экран
    if (loadingScreen) {
      if (loadingScreen._progressInterval) {
        clearInterval(loadingScreen._progressInterval);
      }
      if (loadingScreen._searchTimeout) {
        clearTimeout(loadingScreen._searchTimeout);
      }
      loadingScreen.remove();
      loadingScreen = null;
    }
    
    // Очищаем экран готовности
    if (readyScreen) {
      readyScreen.remove();
      readyScreen = null;
    }
    
    // Очищаем данные
    localPlayer = null;
    remotePlayers = {};
    playerHistory = {};
    visualEffects = [];
    damageNumbers = [];
    projectiles = [];
    moveVector = { x: 0, y: 0 };
    aimVector = { x: 1, y: 0 };
    playerId = null;
    roomId = null;
    team = null;
    gadgetCooldown = 0;
    
    console.log('[MultiplayerBattle] ✅ Все ресурсы очищены');
  }

  return {
    startBattle,
    stopBattle,
    get localPlayer() {
      return localPlayer;
    },
    get team() {
      return team;
    }
  };
}

